import React, { useState } from 'react';
import { SmartphoneIcon, CheckCircleIcon, XCircleIcon, AlertTriangleIcon, PlusIcon, SearchIcon, FilterIcon, RefreshCwIcon, BarChart2Icon } from 'lucide-react';
interface SoftPosTerminal {
  id: string;
  deviceName: string;
  merchantId: string;
  terminalId: string;
  androidVersion: string;
  status: 'active' | 'inactive' | 'pending';
  lastConnection: string;
  transactionVolume: number;
  transactionCount: number;
  model: string;
  owner: string;
  appVersion: string;
}
export function SoftPosTerminals() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  // Mock data for SoftPos terminals
  const terminals: SoftPosTerminal[] = [{
    id: 'softpos-001',
    deviceName: 'Samsung Galaxy S21',
    merchantId: '123456789012',
    terminalId: 'TID-001234',
    androidVersion: '12',
    status: 'active',
    lastConnection: '5 minutes ago',
    transactionVolume: 8750,
    transactionCount: 127,
    model: 'SM-G991B',
    owner: 'John Smith',
    appVersion: '2.3.1'
  }, {
    id: 'softpos-002',
    deviceName: 'Google Pixel 6',
    merchantId: '234567890123',
    terminalId: 'TID-002345',
    androidVersion: '13',
    status: 'active',
    lastConnection: '12 minutes ago',
    transactionVolume: 5230,
    transactionCount: 78,
    model: 'GR1YH',
    owner: 'Sarah Johnson',
    appVersion: '2.3.1'
  }, {
    id: 'softpos-003',
    deviceName: 'Xiaomi Mi 11',
    merchantId: '345678901234',
    terminalId: 'TID-003456',
    androidVersion: '11',
    status: 'inactive',
    lastConnection: '2 days ago',
    transactionVolume: 1820,
    transactionCount: 31,
    model: 'M2011K2G',
    owner: 'Michael Chen',
    appVersion: '2.2.5'
  }, {
    id: 'softpos-004',
    deviceName: 'Samsung Galaxy Tab S7',
    merchantId: '456789012345',
    terminalId: 'TID-004567',
    androidVersion: '12',
    status: 'active',
    lastConnection: '1 hour ago',
    transactionVolume: 12450,
    transactionCount: 189,
    model: 'SM-T870',
    owner: 'Emma Wilson',
    appVersion: '2.3.0'
  }, {
    id: 'softpos-005',
    deviceName: 'OnePlus 9 Pro',
    merchantId: '567890123456',
    terminalId: 'TID-005678',
    androidVersion: '12',
    status: 'pending',
    lastConnection: 'Never',
    transactionVolume: 0,
    transactionCount: 0,
    model: 'LE2121',
    owner: 'David Garcia',
    appVersion: '2.3.1'
  }, {
    id: 'softpos-006',
    deviceName: 'Sony Xperia 1 III',
    merchantId: '678901234567',
    terminalId: 'TID-006789',
    androidVersion: '12',
    status: 'active',
    lastConnection: '25 minutes ago',
    transactionVolume: 3560,
    transactionCount: 42,
    model: 'XQ-BC72',
    owner: 'Lisa Taylor',
    appVersion: '2.3.1'
  }, {
    id: 'softpos-007',
    deviceName: 'Samsung Galaxy A52',
    merchantId: '789012345678',
    terminalId: 'TID-007890',
    androidVersion: '11',
    status: 'inactive',
    lastConnection: '5 days ago',
    transactionVolume: 980,
    transactionCount: 15,
    model: 'SM-A525F',
    owner: 'Robert Brown',
    appVersion: '2.2.0'
  }, {
    id: 'softpos-008',
    deviceName: 'Google Pixel 5',
    merchantId: '890123456789',
    terminalId: 'TID-008901',
    androidVersion: '12',
    status: 'active',
    lastConnection: '3 hours ago',
    transactionVolume: 6740,
    transactionCount: 103,
    model: 'GD1YQ',
    owner: 'Jennifer Martinez',
    appVersion: '2.3.1'
  }];
  // Filter terminals based on search query and status filter
  const filteredTerminals = terminals.filter(terminal => {
    const matchesSearch = terminal.deviceName.toLowerCase().includes(searchQuery.toLowerCase()) || terminal.merchantId.includes(searchQuery) || terminal.terminalId.includes(searchQuery) || terminal.owner.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || terminal.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  // Helper function for status badge
  const getStatusBadge = (status: 'active' | 'inactive' | 'pending') => {
    switch (status) {
      case 'active':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-2369">
            <CheckCircleIcon size={12} className="mr-1" data-id="element-2370" />
            Active
          </span>;
      case 'inactive':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-2371">
            <XCircleIcon size={12} className="mr-1" data-id="element-2372" />
            Inactive
          </span>;
      case 'pending':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-2373">
            <AlertTriangleIcon size={12} className="mr-1" data-id="element-2374" />
            Pending
          </span>;
    }
  };
  return <div className="space-y-6" data-id="element-2375">
      <div className="flex justify-between items-center" data-id="element-2376">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center" data-id="element-2377">
          <SmartphoneIcon size={24} className="mr-2 text-blue-600" data-id="element-2378" />
          SoftPos Terminals
        </h1>
        <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center transition-colors" data-id="element-2379">
          <PlusIcon size={16} className="mr-2" data-id="element-2380" />
          Register New Device
        </button>
      </div>
      {/* Stats overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-2381">
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm" data-id="element-2382">
          <div className="flex items-center" data-id="element-2383">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3" data-id="element-2384">
              <SmartphoneIcon size={20} className="text-blue-600" data-id="element-2385" />
            </div>
            <div data-id="element-2386">
              <div className="text-sm font-medium text-gray-500" data-id="element-2387">
                Total Devices
              </div>
              <div className="text-2xl font-semibold" data-id="element-2388">{terminals.length}</div>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm" data-id="element-2389">
          <div className="flex items-center" data-id="element-2390">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mr-3" data-id="element-2391">
              <CheckCircleIcon size={20} className="text-green-600" data-id="element-2392" />
            </div>
            <div data-id="element-2393">
              <div className="text-sm font-medium text-gray-500" data-id="element-2394">
                Active Devices
              </div>
              <div className="text-2xl font-semibold" data-id="element-2395">
                {terminals.filter(t => t.status === 'active').length}
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm" data-id="element-2396">
          <div className="flex items-center" data-id="element-2397">
            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center mr-3" data-id="element-2398">
              <BarChart2Icon size={20} className="text-purple-600" data-id="element-2399" />
            </div>
            <div data-id="element-2400">
              <div className="text-sm font-medium text-gray-500" data-id="element-2401">
                Total Volume
              </div>
              <div className="text-2xl font-semibold" data-id="element-2402">
                $
                {terminals.reduce((sum, t) => sum + t.transactionVolume, 0).toLocaleString()}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden" data-id="element-2403">
        {/* Filter and search bar */}
        <div className="p-4 border-b border-gray-200 bg-gray-50 flex flex-col sm:flex-row gap-4" data-id="element-2404">
          <div className="relative flex-grow" data-id="element-2405">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2406">
              <SearchIcon size={16} className="text-gray-400" data-id="element-2407" />
            </div>
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Search by device name, MID, TID, or owner..." data-id="element-2408" />
          </div>
          <div className="flex space-x-2" data-id="element-2409">
            <div className="relative" data-id="element-2410">
              <div className="flex items-center" data-id="element-2411">
                <FilterIcon size={16} className="text-gray-500 mr-2" data-id="element-2412" />
                <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="block pl-3 pr-8 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md" data-id="element-2413">
                  <option value="all" data-id="element-2414">All Status</option>
                  <option value="active" data-id="element-2415">Active</option>
                  <option value="inactive" data-id="element-2416">Inactive</option>
                  <option value="pending" data-id="element-2417">Pending</option>
                </select>
              </div>
            </div>
            <button className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-2418">
              <RefreshCwIcon size={16} className="mr-2" data-id="element-2419" />
              Refresh
            </button>
          </div>
        </div>
        {/* Terminal grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4" data-id="element-2420">
          {filteredTerminals.map(terminal => <div key={terminal.id} className="border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow" data-id="element-2421">
              <div className="p-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center" data-id="element-2422">
                <div className="font-medium text-gray-800" data-id="element-2423">
                  {terminal.deviceName}
                </div>
                {getStatusBadge(terminal.status)}
              </div>
              <div className="p-4 space-y-3" data-id="element-2424">
                <div className="flex items-center" data-id="element-2425">
                  <SmartphoneIcon size={36} className="text-blue-600 mr-3" data-id="element-2426" />
                  <div data-id="element-2427">
                    <div className="font-medium" data-id="element-2428">{terminal.model}</div>
                    <div className="text-sm text-gray-600" data-id="element-2429">
                      Android {terminal.androidVersion}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm" data-id="element-2430">
                  <div data-id="element-2431">
                    <div className="text-gray-500" data-id="element-2432">Merchant ID</div>
                    <div data-id="element-2433">{terminal.merchantId}</div>
                  </div>
                  <div data-id="element-2434">
                    <div className="text-gray-500" data-id="element-2435">Terminal ID</div>
                    <div data-id="element-2436">{terminal.terminalId}</div>
                  </div>
                  <div data-id="element-2437">
                    <div className="text-gray-500" data-id="element-2438">App Version</div>
                    <div data-id="element-2439">{terminal.appVersion}</div>
                  </div>
                  <div data-id="element-2440">
                    <div className="text-gray-500" data-id="element-2441">Last Connection</div>
                    <div data-id="element-2442">{terminal.lastConnection}</div>
                  </div>
                  <div data-id="element-2443">
                    <div className="text-gray-500" data-id="element-2444">Transaction Volume</div>
                    <div data-id="element-2445">${terminal.transactionVolume.toLocaleString()}</div>
                  </div>
                  <div data-id="element-2446">
                    <div className="text-gray-500" data-id="element-2447">Transaction Count</div>
                    <div data-id="element-2448">{terminal.transactionCount}</div>
                  </div>
                </div>
                <div className="text-sm" data-id="element-2449">
                  <div className="text-gray-500" data-id="element-2450">Owner</div>
                  <div data-id="element-2451">{terminal.owner}</div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 border-t border-gray-200" data-id="element-2452">
                <div className="flex justify-between" data-id="element-2453">
                  <button className="text-blue-600 hover:text-blue-800 text-sm font-medium" data-id="element-2454">
                    View Details
                  </button>
                  <button className="text-blue-600 hover:text-blue-800 text-sm font-medium" data-id="element-2455">
                    Update App
                  </button>
                </div>
              </div>
            </div>)}
        </div>
        {/* Empty state */}
        {filteredTerminals.length === 0 && <div className="py-12 flex flex-col items-center justify-center text-center" data-id="element-2456">
            <SmartphoneIcon size={48} className="text-gray-400 mb-4" data-id="element-2457" />
            <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-2458">
              No SoftPos terminals found
            </h3>
            <p className="text-gray-500 max-w-md" data-id="element-2459">
              No terminals match your current search criteria. Try adjusting
              your filters or register a new device.
            </p>
          </div>}
      </div>
    </div>;
}